import { useCallback, useEffect, useMemo, useState } from 'react'
import { Section, TableScroller, useBearer, usePagedQuery, useToast } from './_admin-utils'
import { useAuth } from '@/lib/auth-context'
import { supabase } from '@/supabase/client'
import { listTickets, addTicketMessage, getTicket, updateTicket as coreUpdateTicket } from '@/lib/coreApi'
import NumaSummaryChip from '@/components/support/NumaSummaryChip'
import TicketWalletSummary from '@/components/admin/TicketWalletSummary'
import TicketLedgerAdjustmentForm from '@/components/admin/TicketLedgerAdjustmentForm'

interface TicketMessage {
  id: string
  ticket_id: string
  body: string
  sender_role?: string
  visibility?: string
  created_at: string
  metadata?: {
    confidence?: number
    [key: string]: unknown
  }
}

interface TicketMetadata {
  credit_context?: CreditContext
  wallet_context?: string
  [key: string]: unknown
}

interface Ticket {
  id: string
  subject: string
  status: string
  priority: string
  category?: string
  assignee_id?: string
  numa_state?: string
  numa_confidence?: number
  created_at: string
  resolved_notes?: string
  metadata?: TicketMetadata
  ticket_messages?: TicketMessage[]
}

interface Metrics {
  queue_oldest_hours?: number
  avg_first_response_hours?: number
  auto_resolve_pct?: number
}

interface CreditContext {
  account_id?: string
  balance_credits?: number
  snapshot_at?: string
  share_link?: string
  wallet_context?: string
  kind?: string
}

interface LedgerEntry {
  id?: string
  type?: string
  delta_credits?: number
  created_at?: string
  note?: string
  reference_id?: string
}

interface CreditSnapshot {
  context?: CreditContext
  ledger?: LedgerEntry[]
  account?: {
    balance_credits?: number
  }
}

const STATUS_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'queued', label: 'Queued' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'auto_resolved', label: 'Auto-resolved' },
  { value: 'closed', label: 'Closed' },
]

const QUICK_FILTERS = [
  { label: 'Unanswered', value: 'queued' },
  { label: 'Auto-Resolved', value: 'auto_resolved' },
  { label: 'Escalated', value: 'in_progress' },
]

const PRIORITY_OPTIONS = ['low', 'medium', 'high', 'urgent']

const PRIORITY_LABEL: Record<string, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  urgent: 'Urgent',
}

const STATUS_LABEL: Record<string, string> = {
  queued: 'Queued',
  in_progress: 'In progress',
  auto_resolved: 'Auto-resolved',
  closed: 'Closed',
}

function formatDate(value: string | null | undefined): string {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleString()
}

function formatHours(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—'
  const hours = Number(value)
  if (!Number.isFinite(hours)) return '—'
  if (hours >= 24) return `${Math.round(hours)}h`
  return `${hours.toFixed(1)}h`
}

function formatCredits(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—'
  const num = Number(value)
  if (!Number.isFinite(num)) return '—'
  return `${num.toLocaleString()} credits`
}

function formatConfidencePercent(value: number | null | undefined): number | null {
  if (value === null || value === undefined) return null
  const pct = Math.round(Number(value) * 100)
  if (!Number.isFinite(pct)) return null
  return pct
}

export default function AdminTickets() {
  const bearer = useBearer()
  const { user, session } = useAuth()
  const token = session?.access_token || ''
  const [status, setStatus] = useState('queued')
  const [assigneeFilter, setAssigneeFilter] = useState('')
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<Ticket | null>(null)
  const [message, setMessage] = useState('')
  const [replyRole, setReplyRole] = useState('admin')
  const [replyVisibility, setReplyVisibility] = useState('public')
  const { show, ToastHost } = useToast()
  const [queueTickets, setQueueTickets] = useState<Ticket[]>([])
  const [queueLoading, setQueueLoading] = useState(false)
  const [queueError, setQueueError] = useState('')
  const [metrics, setMetrics] = useState<Metrics | null>(null)
  const [mockBanner, setMockBanner] = useState(false)
  const [mockDismissed, setMockDismissed] = useState(false)
  const [sourceFilter, setSourceFilter] = useState('')
  const [creditSnapshot, setCreditSnapshot] = useState<CreditSnapshot | null>(null)
  const [creditLoading, setCreditLoading] = useState(false)
  const [creditError, setCreditError] = useState('')
  const [creditReloadKey, setCreditReloadKey] = useState(0)
  const [adjustDelta, setAdjustDelta] = useState('')
  const [adjustNote, setAdjustNote] = useState('')
  const [adjustBusy, setAdjustBusy] = useState(false)
  const [adjustMessage, setAdjustMessage] = useState('')
  const [adjustMessageTone, setAdjustMessageTone] = useState('neutral')
  const [ledgerAdjustmentId, setLedgerAdjustmentId] = useState('')

  const handleRoleChange = useCallback(
    (role: string) => {
      setReplyRole(role)
      if (role === 'numa') {
        setReplyVisibility('numa')
      } else if (replyVisibility === 'numa') {
        setReplyVisibility('public')
      }
    },
    [replyVisibility]
  )

  const handleMockHeader = useCallback((response: Response | null) => {
    if (!response?.headers) return
    const isMock = response.headers.get('x-mock-data') === 'true'
    setMockBanner(isMock)
    if (!isMock) {
      setMockDismissed(false)
    }
  }, [])

  const fetcher = useMemo(() => {
    return async ({ page, pageSize }: { page: number; pageSize: number }): Promise<{ items: Ticket[]; total: number }> => {
      if (!bearer) return { items: [], total: 0 }
      const result = await listTickets({
        page,
        page_size: pageSize,
        scope: 'admin',
        status: status && status !== 'all' ? status : undefined,
        assignee_id: assigneeFilter.trim() || undefined,
        q: search.trim() || undefined,
        source: sourceFilter || undefined,
      })
      return { items: (result.data.items as unknown as Ticket[]) || [], total: result.data.total || 0 }
    }
  }, [bearer, status, assigneeFilter, search, sourceFilter])

  const { items, page, pageCount, load, loading, error } = usePagedQuery(fetcher, [fetcher], 20)

  const refresh = useCallback(() => load(page), [load, page])

  const loadQueue = useCallback(async () => {
    if (!bearer) return
    setQueueLoading(true)
    setQueueError('')
    try {
      const result = await listTickets({
        status: 'queued',
        priority: 'low',
        page_size: 10,
        scope: 'admin',
      })
      setQueueTickets((result.data.items as unknown as Ticket[]) || [])
    } catch (err) {
      setQueueTickets([])
      setQueueError(err instanceof Error ? err.message : 'Unable to load queue')
    } finally {
      setQueueLoading(false)
    }
  }, [bearer])

  const updateTicket = async (payload: Record<string, unknown>) => {
    if (!bearer || !selected) return
    try {
      const result = await coreUpdateTicket(selected.id, payload as { status?: string; priority?: string; assignee_id?: string | null })
      if (result.data) {
        setSelected((prev) => (prev && prev.id === result.data.id ? { ...prev, ...result.data } : prev))
      }
      show('Ticket updated')
      refresh()
      loadQueue()
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Unable to update ticket')
    }
  }

  const appendMessageToSelected = useCallback(
    (messageRow: TicketMessage) => {
      setSelected((prev) => {
        if (!prev || prev.id !== selected?.id) return prev
        const existing = Array.isArray(prev.ticket_messages) ? prev.ticket_messages : []
        return { ...prev, ticket_messages: [...existing, messageRow] }
      })
    },
    [selected?.id],
  )

  const sendMessage = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!bearer || !selected || !message.trim()) return
    const isInternal = replyVisibility === 'internal'
    try {
      const result = await addTicketMessage(selected.id, {
        body: message,
        visibility: isInternal ? 'internal' : 'public',
      })
      setMessage('')
      if (ledgerAdjustmentId.trim()) {
        setLedgerAdjustmentId('')
      }
      if (replyRole !== 'numa') {
        setReplyVisibility(isInternal ? 'internal' : 'public')
      }
      appendMessageToSelected(result.data as unknown as TicketMessage)
      show(isInternal ? 'Internal note added' : replyRole === 'numa' ? 'NUMA reply sent' : 'Reply sent')
      refresh()
      loadQueue()
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Unable to post message')
    }
  }

  const assignToMe = () => {
    if (!user?.id) return
    updateTicket({ assignee_id: user.id })
  }

  const submitCreditAdjustment = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!token || !selected?.id || !creditContext?.account_id) return
    const deltaValue = Math.trunc(Number(adjustDelta))
    if (!Number.isFinite(deltaValue) || deltaValue === 0) {
      setAdjustMessage('Enter a non-zero credit delta')
      setAdjustMessageTone('error')
      return
    }
    setAdjustBusy(true)
    setAdjustMessage('')
    setAdjustMessageTone('neutral')
    try {
      const CORE_API = import.meta.env.VITE_CORE_API_URL || '';
      const response = await fetch(`${CORE_API}/api/v1/admin/tickets/${selected.id}/ledger-adjust`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          ticket_id: selected.id,
          account_id: creditContext.account_id,
          delta_credits: deltaValue,
          note: adjustNote || undefined,
        }),
      })
      const payload = await response.json().catch(() => ({}))
      if (!response.ok) {
        throw new Error(payload?.error || 'Unable to log adjustment')
      }
      const ledgerEntryId = payload?.ledger_adjustment_id ?? payload?.adjustment?.ledger_entry_id
      const deltaCredits = payload?.adjustment?.delta_credits ?? deltaValue
      setAdjustDelta('')
      setAdjustNote('')
      setAdjustMessage('Adjustment logged')
      setAdjustMessageTone('success')
      show('Credit adjustment logged')
      setCreditReloadKey((value) => value + 1)
      if (ledgerEntryId && bearer) {
        const replyBody = deltaCredits
          ? `Ledger adjustment logged: ${deltaCredits > 0 ? '+' : ''}${deltaCredits} credits` +
            (adjustNote ? ` (${adjustNote})` : '')
          : 'Ledger adjustment logged'
        try {
          const replyResult = await addTicketMessage(selected.id, {
            body: replyBody,
            visibility: 'internal',
          })
          if (replyResult.data) {
            appendMessageToSelected(replyResult.data as unknown as TicketMessage)
          }
        } catch (noteErr) {
          setAdjustMessage(
            noteErr instanceof Error ? noteErr.message : 'Adjustment logged but failed to attach ledger marker to the ticket',
          )
          setAdjustMessageTone('error')
        }
      }
    } catch (err) {
      setAdjustMessage(err instanceof Error ? err.message : 'Unable to log adjustment')
      setAdjustMessageTone('error')
    } finally {
      setAdjustBusy(false)
    }
  }

  const lastNumaMessage = useMemo(() => {
    if (!selected?.ticket_messages) return null
    return (
      [...selected.ticket_messages]
        .filter((msg) => msg.sender_role === 'numa')
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0] || null
    )
  }, [selected?.ticket_messages])

  const currentConfidence = useMemo(() => {
    if (typeof selected?.numa_confidence === 'number') {
      return formatConfidencePercent(selected.numa_confidence)
    }
    if (typeof lastNumaMessage?.metadata?.confidence === 'number') {
      return formatConfidencePercent(lastNumaMessage.metadata.confidence)
    }
    return null
  }, [selected?.numa_confidence, lastNumaMessage])

  const creditContext = selected?.metadata?.credit_context || null
  const effectiveCreditContext = creditSnapshot?.context || creditContext
  const creditLedger = Array.isArray(creditSnapshot?.ledger) ? creditSnapshot.ledger : []
  const creditBalance =
    creditSnapshot?.account?.balance_credits ?? effectiveCreditContext?.balance_credits ?? null
  const isMoneyTicket = ['credits', 'billing'].includes(selected?.category ?? '')

  const formattedMetrics = useMemo(() => {
    if (!metrics) return null
    const pct = Number(metrics.auto_resolve_pct)
    return {
      queueAge: formatHours(metrics.queue_oldest_hours),
      firstResponse: formatHours(metrics.avg_first_response_hours),
      autoResolve: Number.isFinite(pct) ? `${pct.toFixed(1)}%` : '—',
    }
  }, [metrics])

  const loadSelectedMessages = useCallback(async () => {
    if (!token || !selected?.id) return
    try {
      const result = await getTicket(selected.id)
      const msgs = (result.data.messages as unknown as TicketMessage[]) || []
      setSelected((prev) => {
        if (!prev || prev.id !== selected.id) return prev
        return { ...prev, ticket_messages: msgs }
      })
    } catch {
      // silent fail for messages
    }
  }, [token, selected?.id])

  useEffect(() => {
    loadQueue()
  }, [loadQueue])

  useEffect(() => {
    if (!selected) return
    const latest = items.find((ticket) => ticket.id === selected.id)
    if (latest && latest !== selected) setSelected(latest)
  }, [items, selected])

  useEffect(() => {
    loadSelectedMessages()
  }, [loadSelectedMessages])

  useEffect(() => {
    if (!selected?.id) return
    const channel = supabase
      .channel(`admin-ticket-${selected.id}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'ledger', table: 'ticket_messages', filter: `ticket_id=eq.${selected.id}` },
        () => loadSelectedMessages()
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'ledger', table: 'tickets', filter: `id=eq.${selected.id}` },
        (payload) => {
          const newData = payload?.new as Ticket | undefined
          if (newData) {
            setSelected((prev) => (prev && prev.id === newData.id ? { ...prev, ...newData } : prev))
          }
        }
      )

    channel.subscribe()
    return () => {
      channel.unsubscribe()
    }
  }, [selected?.id, loadSelectedMessages])

  useEffect(() => {
    if (!creditContext?.account_id || !selected?.id || !token) {
      if (!creditContext?.account_id) {
        setCreditSnapshot(null)
        setCreditError('')
      }
      setCreditLoading(false)
      return
    }

    let cancelled = false
    setCreditLoading(true)
    setCreditError('')

    const CORE_API2 = import.meta.env.VITE_CORE_API_URL || '';
    fetch(`${CORE_API2}/api/v1/admin/tickets/${selected.id}/wallet-snapshot`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (response) => {
        const payload = await response.json().catch(() => ({}))
        if (!response.ok) {
          throw new Error(payload?.error || 'Unable to load credit snapshot')
        }
        if (!cancelled) {
          setCreditSnapshot(payload)
        }
      })
      .catch((err) => {
        if (cancelled) return
        setCreditSnapshot(null)
        setCreditError(err?.message || 'Unable to load credit snapshot')
      })
      .finally(() => {
        if (!cancelled) {
          setCreditLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [creditContext?.account_id, selected?.id, token, creditReloadKey])

  useEffect(() => {
    if (!creditContext?.account_id) {
      setAdjustDelta('')
      setAdjustNote('')
      setAdjustMessage('')
      setAdjustMessageTone('neutral')
    }
  }, [creditContext?.account_id])

  useEffect(() => {
    setMessage('')
    setReplyRole('admin')
    setReplyVisibility('public')
  }, [selected?.id])

  return (
    <Section
      title="Support Tickets"
      actions={
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex flex-wrap items-center gap-2 text-xs uppercase tracking-wide text-cocoa/60">
            {STATUS_OPTIONS.map(({ value, label }) => (
              <button
                key={value}
                type="button"
                onClick={() => {
                  setStatus(value)
                  load(1)
                }}
                className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
                  status === value ? 'bg-cocoa text-white' : 'bg-shell/60 text-cocoa/70 hover:bg-shell'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-cocoa/50">
            {QUICK_FILTERS.map((filter) => (
              <button
                key={filter.value}
                type="button"
                onClick={() => {
                  setStatus(filter.value)
                  setSourceFilter('')
                  load(1)
                }}
                className={`rounded-full border px-3 py-1 transition ${
                  status === filter.value ? 'border-cocoa bg-cocoa text-white' : 'border-white/70 text-cocoa hover:bg-shell'
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => {
              const next = sourceFilter === 'numa' ? '' : 'numa'
              setSourceFilter(next)
              load(1)
            }}
            className={`rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-wide transition ${
              sourceFilter === 'numa'
                ? 'border-emerald-500 bg-emerald-500 text-white'
                : 'border-white/70 text-cocoa hover:bg-shell'
            }`}
          >
            NUMA Auto
          </button>
          <input
            type="text"
            value={assigneeFilter}
            onChange={(event) => setAssigneeFilter(event.target.value)}
            placeholder="Assignee ID"
            className="rounded-lg border border-white/70 bg-shell/40 px-3 py-2 text-sm"
          />
          <input
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search subject"
            className="rounded-lg border border-white/70 bg-shell/40 px-3 py-2 text-sm"
          />
          <button
            type="button"
            onClick={() => load(1)}
            className="rounded-full border border-white/70 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-cocoa hover:bg-shell"
          >
            Apply
          </button>
        </div>
      }
    >
      <ToastHost />
      {mockBanner && !mockDismissed ? (
        <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="font-semibold">Supabase not configured – mock support data in use.</p>
              <p className="text-xs text-amber-700">
                Connect Supabase credentials to resume live ticketing data.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setMockDismissed(true)}
              className="rounded-full border border-amber-300 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-amber-800 hover:bg-amber-100"
            >
              Dismiss
            </button>
          </div>
        </div>
      ) : null}
      {formattedMetrics ? (
        <div className="mb-4 grid gap-3 sm:grid-cols-3">
          <div className="rounded-2xl border border-white/70 bg-white/80 p-4 text-sm text-cocoa/80">
            <p className="text-xs uppercase tracking-wide text-cocoa/50">Oldest queue age</p>
            <p className="mt-1 text-2xl font-semibold text-cocoa">{formattedMetrics.queueAge}</p>
          </div>
          <div className="rounded-2xl border border-white/70 bg-white/80 p-4 text-sm text-cocoa/80">
            <p className="text-xs uppercase tracking-wide text-cocoa/50">Avg. first response (30d)</p>
            <p className="mt-1 text-2xl font-semibold text-cocoa">{formattedMetrics.firstResponse}</p>
          </div>
          <div className="rounded-2xl border border-white/70 bg-white/80 p-4 text-sm text-cocoa/80">
            <p className="text-xs uppercase tracking-wide text-cocoa/50">Auto-resolve rate (30d)</p>
            <p className="mt-1 text-2xl font-semibold text-cocoa">{formattedMetrics.autoResolve}</p>
          </div>
        </div>
      ) : null}
      <div className="grid gap-6 lg:grid-cols-[1.1fr_1fr]">
        <div>
          {loading ? (
            <p className="text-sm text-cocoa/70">Loading tickets…</p>
          ) : error ? (
            <p className="text-sm text-brick">{error}</p>
          ) : items.length ? (
            <TableScroller>
              <table className="min-w-full divide-y divide-white/60 text-sm text-cocoa/80">
                <thead className="bg-shell/60 text-xs uppercase tracking-wide text-cocoa/60">
                  <tr>
                    <th className="px-3 py-2 text-left">Subject</th>
                    <th className="px-3 py-2 text-left">Priority</th>
                    <th className="px-3 py-2 text-left">Status</th>
                    <th className="px-3 py-2 text-left">Assignee</th>
                    <th className="px-3 py-2 text-left">NUMA</th>
                    <th className="px-3 py-2 text-left">Created</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/60">
                  {items.map((ticket) => (
                    <tr
                      key={ticket.id}
                      className={`cursor-pointer transition ${selected?.id === ticket.id ? 'bg-shell/60' : 'hover:bg-shell/40'}`}
                      onClick={() => setSelected(ticket)}
                    >
                      <td className="px-3 py-3 font-medium text-cocoa">{ticket.subject}</td>
                      <td className="px-3 py-3 capitalize">{PRIORITY_LABEL[ticket.priority] || ticket.priority}</td>
                      <td className="px-3 py-3 capitalize">{STATUS_LABEL[ticket.status] || ticket.status}</td>
                      <td className="px-3 py-3 text-xs text-cocoa/60">{ticket.assignee_id || '—'}</td>
                      <td className="px-3 py-3 text-xs text-cocoa/60">
                        {ticket.numa_state ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-emerald-700">
                            {ticket.numa_state}
                            {typeof ticket.numa_confidence === 'number' ? (
                              <span>{formatConfidencePercent(ticket.numa_confidence)}%</span>
                            ) : null}
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="px-3 py-3 text-xs text-cocoa/60">{formatDate(ticket.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableScroller>
          ) : (
            <p className="text-sm text-cocoa/70">No tickets found.</p>
          )}
          <div className="mt-4 flex justify-end gap-3 text-xs uppercase tracking-wide text-cocoa/60">
            <button
              type="button"
              onClick={() => load(Math.max(page - 1, 1))}
              disabled={page <= 1}
              className="rounded-full border border-white/70 px-3 py-1.5 text-cocoa disabled:opacity-50"
            >
              Previous
            </button>
            <span>
              Page {page} of {pageCount}
            </span>
            <button
              type="button"
              onClick={() => load(Math.min(page + 1, pageCount))}
              disabled={page >= pageCount}
              className="rounded-full border border-white/70 px-3 py-1.5 text-cocoa disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>

        <div className="space-y-4">
          <div className="rounded-2xl border border-white/70 bg-white p-4 shadow-sm">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-cocoa">Low-priority queue</h3>
              <button
                type="button"
                onClick={loadQueue}
                className="rounded-full border border-white/60 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-cocoa hover:bg-shell"
              >
                Refresh
              </button>
            </div>
            {queueLoading ? (
              <p className="text-xs text-cocoa/70">Loading…</p>
            ) : queueError ? (
              <p className="text-xs text-brick">{queueError}</p>
            ) : queueTickets.length ? (
              <ul className="space-y-2">
                {queueTickets.map((ticket) => (
                  <li key={ticket.id}>
                    <button
                      type="button"
                      onClick={() => setSelected(ticket)}
                      className={`w-full rounded-xl border px-3 py-2 text-left text-xs transition ${
                        selected?.id === ticket.id
                          ? 'border-cocoa bg-cocoa/5 text-cocoa'
                          : 'border-sand bg-shell/30 text-cocoa/80 hover:bg-shell'
                      }`}
                    >
                      <p className="font-semibold text-cocoa">{ticket.subject}</p>
                      <p className="text-[11px] uppercase tracking-wide text-cocoa/50">
                        {formatDate(ticket.created_at)} • {ticket.category || 'General'}
                      </p>
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-xs text-cocoa/60">No low-priority tickets waiting.</p>
            )}
          </div>

          {selected ? (
            <div className="rounded-2xl border border-white/70 bg-white p-4 shadow-sm">
              <header className="space-y-1">
                <h2 className="text-lg font-semibold text-cocoa">{selected.subject}</h2>
                <p className="text-sm text-cocoa/60">Created {formatDate(selected.created_at)}</p>
                <p className="text-xs uppercase tracking-wide text-cocoa/50">
                  {(STATUS_LABEL[selected.status] || selected.status || '').toUpperCase()} •{' '}
                  {PRIORITY_LABEL[selected.priority] || selected.priority || 'Medium'} Priority
                </p>
                <p className="text-xs uppercase tracking-wide text-cocoa/50">
                  NUMA state: {selected.numa_state || 'triage'}
                  {currentConfidence !== null ? ` • ${currentConfidence}% confidence` : ''}
                </p>
                {selected.resolved_notes ? (
                  <p className="text-xs text-cocoa/70">Resolution notes: {selected.resolved_notes}</p>
                ) : null}
              </header>
              <div className="mt-3">
                <TicketWalletSummary ticketId={selected.id} bearer={bearer} />
              </div>
              <div className="mt-3">
                <TicketLedgerAdjustmentForm
                  ticketId={selected.id}
                  bearer={bearer}
                  walletContext={
                    selected.metadata?.wallet_context ||
                    selected.metadata?.credit_context?.wallet_context ||
                    selected.metadata?.credit_context?.kind ||
                    null
                  }
                />
              </div>
              {token ? <NumaSummaryChip ticketId={selected.id} token={token} className="mt-3" /> : null}
              {lastNumaMessage ? (
                <div className="mt-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
                  <p className="text-xs uppercase tracking-wide text-emerald-700">
                    Latest NUMA reply • {formatDate(lastNumaMessage.created_at)}
                    {currentConfidence !== null ? ` • ${currentConfidence}% confidence` : ''}
                  </p>
                  <p className="mt-2 whitespace-pre-wrap">{lastNumaMessage.body}</p>
                </div>
              ) : null}
              {creditContext ? (
                <div className="mt-3 space-y-3 rounded-2xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-xs uppercase tracking-wide text-amber-700">Credit snapshot</p>
                    <span className="text-[11px] uppercase tracking-wide text-amber-700">
                      Account {creditContext.account_id}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-3 text-sm">
                    <div>
                      <p className="text-[11px] uppercase tracking-wide text-amber-700">Current balance</p>
                      <p className="text-base font-semibold text-amber-900">{formatCredits(creditBalance)}</p>
                    </div>
                    {effectiveCreditContext?.snapshot_at ? (
                      <p className="text-xs text-amber-700">
                        Snapshot {formatDate(effectiveCreditContext.snapshot_at)}
                      </p>
                    ) : null}
                    {effectiveCreditContext?.share_link ? (
                      <a
                        href={effectiveCreditContext.share_link}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs font-semibold text-amber-800 underline"
                      >
                        Open share link
                      </a>
                    ) : null}
                  </div>
                  <div>
                    <p className="text-[11px] uppercase tracking-wide text-amber-700">Recent ledger entries</p>
                    {creditLoading ? (
                      <p className="text-xs text-amber-700">Loading snapshot…</p>
                    ) : creditError ? (
                      <p className="text-xs text-brick">{creditError}</p>
                    ) : creditLedger.length ? (
                      <ul className="mt-2 space-y-2">
                        {creditLedger.map((entry) => {
                          const delta = Number(entry.delta_credits ?? 0)
                          const hasDelta = Number.isFinite(delta)
                          const positive = delta >= 0
                          const displayDelta = hasDelta
                            ? `${positive ? '+' : '−'}${Math.abs(delta).toLocaleString()} credits`
                            : '—'
                          return (
                            <li
                              key={entry.id || `${entry.created_at}-${entry.note || ''}`}
                              className="rounded-xl border border-amber-200 bg-white/70 p-2 text-xs text-cocoa"
                            >
                              <div className="flex items-center justify-between gap-2">
                                <span className="font-semibold text-cocoa">{entry.type || 'Entry'}</span>
                                <span className={positive ? 'text-emerald-700' : 'text-brick'}>{displayDelta}</span>
                              </div>
                              <p className="text-[11px] text-cocoa/60">
                                {entry.created_at ? formatDate(entry.created_at) : '—'}
                              </p>
                              {entry.note ? (
                                <p className="text-[11px] text-cocoa/70">{entry.note}</p>
                              ) : null}
                              {entry.reference_id ? (
                                <p className="text-[11px] text-cocoa/60">Ref: {entry.reference_id}</p>
                              ) : null}
                            </li>
                          )
                        })}
                      </ul>
                    ) : (
                      <p className="text-xs text-amber-700">No ledger entries yet.</p>
                    )}
                  </div>
                  <form onSubmit={submitCreditAdjustment} className="space-y-2 border-t border-amber-200 pt-3 text-xs">
                    <p className="text-[11px] uppercase tracking-wide text-amber-700">Log credit adjustment</p>
                    <div className="flex flex-col gap-2 sm:flex-row">
                      <input
                        type="number"
                        value={adjustDelta}
                        onChange={(event) => setAdjustDelta(event.target.value)}
                        placeholder="Δ credits"
                        className="w-full rounded-lg border border-amber-200 bg-white/80 px-2 py-1"
                      />
                      <input
                        type="text"
                        value={adjustNote}
                        onChange={(event) => setAdjustNote(event.target.value)}
                        placeholder="Note"
                        className="w-full rounded-lg border border-amber-200 bg-white/80 px-2 py-1"
                      />
                      <button
                        type="submit"
                        disabled={adjustBusy || !adjustDelta.trim()}
                        className="rounded-full bg-cocoa px-3 py-1 font-semibold text-white disabled:opacity-60"
                      >
                        {adjustBusy ? 'Saving…' : 'Log adjustment'}
                      </button>
                    </div>
                    {adjustMessage ? (
                      <p
                        className={`text-[11px] ${
                          adjustMessageTone === 'error' ? 'text-brick' : 'text-emerald-700'
                        }`}
                      >
                        {adjustMessage}
                      </p>
                    ) : null}
                  </form>
                </div>
              ) : null}
              <div className="mt-3 grid gap-2 text-sm text-cocoa/70">
                <label className="flex items-center gap-2">
                  <span className="text-xs uppercase tracking-wide">Status</span>
                  <select
                    value={selected.status}
                    onChange={(event) => updateTicket({ status: event.target.value })}
                    className="rounded-lg border border-white/70 bg-shell/40 px-2 py-1 text-xs"
                  >
                    {STATUS_OPTIONS.filter((option) => option.value !== 'all').map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="flex items-center gap-2">
                  <span className="text-xs uppercase tracking-wide">Priority</span>
                  <select
                    value={selected.priority}
                    onChange={(event) => updateTicket({ priority: event.target.value })}
                    className="rounded-lg border border-white/70 bg-shell/40 px-2 py-1 text-xs"
                  >
                    {PRIORITY_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {PRIORITY_LABEL[option] || option}
                      </option>
                    ))}
                  </select>
                </label>
                <div className="flex items-center gap-2">
                  <span className="text-xs uppercase tracking-wide">Assignee</span>
                  <input
                    type="text"
                    value={selected.assignee_id || ''}
                    onChange={(event) => updateTicket({ assignee_id: event.target.value || null })}
                    placeholder="User ID"
                    className="w-full rounded-lg border border-white/70 bg-shell/40 px-2 py-1 text-xs"
                  />
                  <button
                    type="button"
                    onClick={assignToMe}
                    className="rounded-full border border-white/70 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-cocoa hover:bg-shell"
                  >
                    Assign to me
                  </button>
                </div>
              </div>
              <div className="mt-4 space-y-3">
                {(selected.ticket_messages || [])
                  .slice()
                  .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
                  .map((msg) => {
                  const visibilityLabel =
                    msg.visibility === 'internal'
                      ? 'Internal note'
                      : msg.visibility === 'numa'
                      ? 'NUMA thread'
                      : 'Public reply'
                  return (
                    <div
                      key={msg.id}
                      className={`rounded-xl border border-white/70 p-3 text-sm ${
                        msg.visibility === 'internal'
                          ? 'bg-indigo-50 text-indigo-900'
                          : msg.sender_role === 'numa'
                          ? 'bg-emerald-50 text-emerald-900'
                          : 'bg-shell/40 text-cocoa/80'
                      }`}
                    >
                      <p className="text-xs uppercase tracking-wide text-cocoa/60">
                        {formatDate(msg.created_at)} • {msg.sender_role?.toUpperCase?.() || 'USER'}
                      </p>
                      <p className="mt-1 text-[11px] font-semibold uppercase tracking-wide text-cocoa/50">{visibilityLabel}</p>
                      <p className="mt-2 whitespace-pre-wrap">{msg.body}</p>
                    </div>
                  )
                })}
              </div>
              <form onSubmit={sendMessage} className="mt-4 space-y-3">
                <div className="flex flex-wrap gap-2 text-xs font-semibold uppercase tracking-wide">
                  <button
                    type="button"
                    onClick={() => handleRoleChange('admin')}
                    className={`rounded-full px-3 py-1 ${
                      replyRole === 'admin' ? 'bg-cocoa text-white' : 'bg-shell/60 text-cocoa hover:bg-shell'
                    }`}
                  >
                    Reply as Admin
                  </button>
                  <button
                    type="button"
                    onClick={() => handleRoleChange('numa')}
                    className={`rounded-full px-3 py-1 ${
                      replyRole === 'numa' ? 'bg-emerald-600 text-white' : 'bg-shell/60 text-cocoa hover:bg-shell'
                    }`}
                  >
                    Reply as NUMA
                  </button>
                </div>
                <textarea
                  value={message}
                  onChange={(event) => setMessage(event.target.value)}
                  placeholder="Leave a reply"
                  className="min-h-[120px] w-full rounded-lg border border-white/70 bg-shell/20 px-3 py-2 text-sm"
                />
                {isMoneyTicket ? (
                  <div className="space-y-1 text-xs text-cocoa/70">
                    <label className="flex flex-col gap-1">
                      <span className="font-semibold uppercase tracking-wide">Ledger adjustment ID (optional)</span>
                      <input
                        type="text"
                        value={ledgerAdjustmentId}
                        onChange={(event) => setLedgerAdjustmentId(event.target.value)}
                        placeholder="Paste ledger entry id"
                        className="w-full rounded-lg border border-white/70 bg-white/60 px-2 py-1 text-sm"
                      />
                    </label>
                    <p className="text-[11px] text-cocoa/60">
                      Attach the ledger entry id for billing/credit changes so the ticket can close safely.
                    </p>
                  </div>
                ) : null}
                {replyRole !== 'numa' ? (
                  <label className="flex items-center gap-2 text-xs text-cocoa/70">
                    <input
                      type="checkbox"
                      checked={replyVisibility === 'internal'}
                      onChange={(event) => setReplyVisibility(event.target.checked ? 'internal' : 'public')}
                    />
                    Internal note
                  </label>
                ) : (
                  <p className="text-xs text-emerald-700">NUMA replies stay in the automation thread.</p>
                )}
                <button
                  type="submit"
                  disabled={!message.trim()}
                  className="rounded-full bg-cocoa px-5 py-2 text-sm font-semibold text-white hover:bg-cocoa/90 disabled:opacity-60"
                >
                  Post update
                </button>
              </form>
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-white/70 p-6 text-sm text-cocoa/60">
              Select a ticket to view the conversation.
            </div>
          )}
        </div>
      </div>
    </Section>
  )
}
